import {
  useState,
  useEffect,
} from 'react';
import ImageCropper from '@/components/ImageCropper';
import {
  Modal,
} from '@/components/Modals';
import type {
  ModalProps,
} from '@/components/Modals';
import crop from '@/utils/crop';

import styles from './CropImageModal.module.css';

type CropImageModalProps = ModalProps & {
  image?: string;
  aspect?: number;
  showGrid?: boolean;
  cropShape?: 'rect' | 'round';
  maxHeight?: string;
  onCrop?: (image: string) => void;
}

const DEFAULT_AREA = {
  x: 0,
  y: 0,
  width: 200,
  height: 200,
};

const CropImageModal = (props: CropImageModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    isOpen,
    image,
    aspect = 1,
    showGrid = false,
    cropShape = 'rect',
    maxHeight,
    onCrop,
    onClose,
    ...rest
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [area, setArea] = useState(DEFAULT_AREA);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleCropComplete = (_, areaPixels) => setArea(areaPixels);

  const handleSubmit = async () => {
    const cropped = await crop(image, area);
    onCrop(cropped);
    onClose();
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (!isOpen) return;

    setArea(DEFAULT_AREA);
  }, [isOpen]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Modal
      {...rest}
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={handleSubmit}
      className={styles.cropImageModal}
      contentClassName={styles.content}
    >
      <ImageCropper
        min={1}
        max={3}
        step={0.1}
        aspect={aspect}
        image={image}
        cropShape={cropShape}
        showGrid={showGrid}
        objectFit="cover"
        maxHeight={maxHeight}
        onCropComplete={handleCropComplete}
      />
    </Modal>
  );
};

export default CropImageModal;
